import type { BoxProps } from '@chakra-ui/react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { Mitopure } from '@time-line/components/icons';
import { useImage } from 'lib/sanity';
import Img from 'next/image';
import type { BeforeAfterResultItem as BeforeAfterResultItemType } from 'types/cms/blocks';

import { SLIDER_CONTAINER_BREAKPOINT } from '.';

type SliderItemProps = BoxProps &
  BeforeAfterResultItemType & {
    isAfter?: boolean;
    showOverlay?: boolean;
  };

export function SliderItem({
  image,
  imageOverlay,
  caption,
  isAfter,
  showOverlay,
  ...props
}: SliderItemProps) {
  const imageProps = useImage(image, { ratio: 1 / 1 });
  const imageOverlayProps = useImage(imageOverlay || null, { ratio: 1 / 1 });

  return (
    <Box {...props}>
      {imageProps && (
        <Img {...imageProps} sizes="(min-width: 960px) 33vw, 90vw" />
      )}
      {imageOverlay && imageOverlayProps && showOverlay && (
        <Box pos="absolute" w="full" h="full" top={0} left={0}>
          <Img {...imageOverlayProps} sizes="(min-width: 960px) 33vw, 90vw" />
        </Box>
      )}
      <Flex
        pos="absolute"
        top="2xs"
        left={isAfter ? 'auto' : '2xs'}
        right={isAfter ? '2xs' : 'auto'}
        px="3xs"
        pr={isAfter ? '5xs' : '3xs'}
        py="5xs"
        bg="gray.50"
        borderRadius="full"
        alignItems="center"
        gap="5xs"
        boxShadow="0 0 0 1px rgba(0, 0, 0, 0.08), 0px 0px 0px 1px rgba(0, 0, 0, 0.02), 0px 1px 4px 0px rgba(0, 0, 0, 0.06), 0px 6px 24px 0px rgba(0, 0, 0, 0.06)"
        sx={{
          [`@container (min-width: ${SLIDER_CONTAINER_BREAKPOINT})`]: {
            top: 'sm',
            gap: '4xs',
            ...(isAfter
              ? { right: 'sm', left: 'auto' }
              : { left: 'sm', right: 'auto' }),
          },
        }}
      >
        <Text size="overline" color="gray.600">
          {caption}
        </Text>
        {isAfter && (
          <Mitopure
            width="auto"
            height="18px"
            color="red.300"
            sx={{
              [`@container (min-width: ${SLIDER_CONTAINER_BREAKPOINT})`]: {
                height: 'xs',
              },
            }}
          />
        )}
      </Flex>
    </Box>
  );
}
